import { useRouteNavigator } from '@vkontakte/vk-mini-apps-router';
import type { NavIdProps } from '@vkontakte/vkui';
import { useSession } from '../../api/session';
import { ROLE_LABELS } from '../../api/types';
import { t } from '../../i18n';
import { AccountIcon, ExternalIcon } from '../../shared/icons';
import { digitGroups } from '../../shared/lib/docNumber';
import {
  Card,
  CardHeading,
  CardList,
  Divider,
  ExternalLink,
  Gap,
  Gutter,
  Media,
  Meta,
  Muted,
  NavCard,
  Screen,
  Section,
  SectionTitle,
  StatusBadge,
  type StatusTone,
} from '../../shared/ui';
import { isStaffSession, vkProfileUrl } from '../review/staffKit';

/**
 * Вкладка «Ещё»: всё, чему не нашлось места в основных вкладках.
 *
 * Правила площадки и удаление данных стоят здесь не для галочки — их обязан
 * найти любой пользователь, а не только тот, кто дошёл до отказа модератора.
 */
export function MorePanel({ id }: NavIdProps) {
  const routeNavigator = useRouteNavigator();
  const session = useSession();
  const user = session.user;

  const access: { tone: StatusTone; label: string } =
    user === null
      ? { tone: 'archived', label: t('more.account.readOnly') }
      : user.canWrite
        ? { tone: 'published', label: t('common.access.granted') }
        : { tone: 'rejected', label: t('common.access.revoked') };

  return (
    <Screen id={id} title={t('more.title')}>
      <Section>
        <SectionTitle>{t('more.section.account')}</SectionTitle>
        <CardList>
          {/* Крупно то, что решает, что человеку тут можно: роль и право
              записи. Номер страницы ВК — служебная мелочь под ними. */}
          <Card tone="published">
            <Media aside={<AccountIcon />}>
              <CardHeading badge={<StatusBadge tone={access.tone}>{access.label}</StatusBadge>}>
                {user ? ROLE_LABELS[user.role] : t('more.account.guest')}
              </CardHeading>
              <Gap step="s1h" />
              <Meta>
                {[
                  t('common.vkProfile'),
                  <ExternalLink key="vk" href={vkProfileUrl(session.vkUserId)}>
                    {t('more.account.vkId', { id: digitGroups(session.vkUserId) })}{' '}
                    <ExternalIcon tone="accent" />
                  </ExternalLink>,
                ]}
              </Meta>
            </Media>
          </Card>
        </CardList>
      </Section>

      <Section>
        <SectionTitle>{t('more.section.links')}</SectionTitle>
        <CardList>
          {/* Контур красит РОЛЬ раздела, как в макете: своё — бирюзой,
              персоналу — лиловым, необратимое — малиновым. Четыре одинаковые
              карточки подряд читались одним списком слов. */}
          <NavCard
            tone="published"
            title={t('more.nav.profile.title')}
            hint={t('more.nav.profile.hint')}
            onOpen={() => routeNavigator.push('/more/profile')}
          />

          <NavCard
            title={t('more.nav.rules.title')}
            hint={t('more.nav.rules.hint')}
            onOpen={() => routeNavigator.push('/more/rules')}
          />

          {isStaffSession(session) ? (
            <NavCard
              tone="pending"
              title={t('more.nav.admin.title')}
              hint={t('more.nav.admin.hint')}
              onOpen={() => routeNavigator.push('/more/admin')}
            />
          ) : null}

          {/* Контур отказа: удаление необратимо, и это видно раньше, чем
              человек прочтёт подпись. */}
          <NavCard
            tone="rejected"
            title={t('more.nav.dataDeletion.title')}
            hint={t('more.nav.dataDeletion.hint')}
            onOpen={() => routeNavigator.push('/more/data-deletion')}
          />
        </CardList>
      </Section>

      <Section>
        <Gutter>
          {/* Черта перед подписью: она относится ко всему экрану, а не к
              последнему разделу над ней. */}
          <Divider />
          <Gap step="s3" />
          <Muted>{t('more.about')}</Muted>
        </Gutter>
      </Section>
    </Screen>
  );
}
